import React from "react";
import {
  CompletedIcon,
  ClockIcon,
  CrossIcon,
  ReopenIcon,
} from "../../images/icons/icons";

// Custom Tickets Raised Icon
const TicketsRaisedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M4 2C3.44772 2 3 2.44772 3 3V17C3 17.5523 3.44772 18 4 18H16C16.5523 18 17 17.5523 17 17V6.41421C17 6.149 16.8946 5.89464 16.7071 5.70711L14.2929 3.29289C14.1054 3.10536 13.851 3 13.5858 3H4Z"
      fill="currentColor"
    />
    <path
      d="M6 8H14M6 11H14M6 14H11"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="15"
      cy="5"
      r="3"
      fill="#dc2626"
      stroke="white"
      strokeWidth="1.5"
    />
    <path
      d="M13.5 5H16.5M15 3.5V6.5"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

// Custom Assignment Icon
const AssignmentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M9 6H15M9 10H15M9 14H13M5 6H6M5 10H6M5 14H6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 5C3 3.89543 3.89543 3 5 3H15C16.1046 3 17 3.89543 17 5V15C17 16.1046 16.1046 17 15 17H5C3.89543 17 3 16.1046 3 15V5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

// Custom In Progress Icon
const InProgressIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 6V10L13 13M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom Infrastructure Spend Icon
const InfraSpendIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 2L13 6H16L20 10L16 14H13L10 18L7 14H4L0 10L4 6H7L10 2Z"
      fill="currentColor"
    />
    <path
      d="M8 8V12M10 6V14M12 8V12"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <text
      x="10"
      y="10.5"
      textAnchor="middle"
      fill="white"
      fontSize="6"
      fontWeight="bold"
    >
      ₹
    </text>
  </svg>
);

// Custom Top Performer Icon
const TopPerformerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 1L12.09 6.26L18 7L13 11.74L14.18 17.5L10 15.27L5.82 17.5L7 11.74L2 7L7.91 6.26L10 1Z"
      fill="currentColor"
    />
    <path
      d="M10 5V15M7 8L13 12M13 8L7 12"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

// Custom Efficiency Icon
const EfficiencyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 2C14.418 2 18 5.582 18 10C18 14.418 14.418 18 10 18C5.582 18 2 14.418 2 10C2 5.582 5.582 2 10 2Z"
      fill="currentColor"
    />
    <path
      d="M7 10L9 12L13 8"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StatsCardSection = ({ stats = {} }) => {
  if (!stats) return null;

  // Check if this is manager stats
  const isManagerStats = "ticketsAwaitingAssignment" in stats;
  // Check if this is supervisor stats
  const isSupervisorStats = "assignedTickets" in stats;
  // Check if this is admin stats
  const isAdminStats = "infraSpend" in stats;

  const statsData = isAdminStats
    ? [
        {
          id: "total",
          title: "Total Tickets",
          subtitle: "System-wide",
          value: stats.totalTickets || 0,
          icon: <AssignmentIcon />,
        },
        {
          id: "efficiency",
          title: "Closure Rate",
          subtitle: `${stats.closedPercentage}%`,
          value: `${stats.ticketsClosed}/${stats.totalTickets}`,
          icon: <EfficiencyIcon />,
        },
        {
          id: "performance",
          title: "Avg TAT (Assign to Resolve)",
          subtitle: "Hours",
          value: `${parseFloat(stats.avgTatHours || 0).toFixed(1)}h`,
          icon: <ClockIcon />,
        },
        {
          id: "quality",
          title: "Reopened",
          subtitle: "Quality Check",
          value: stats.ticketsReopened || 0,
          icon: <ReopenIcon />,
        },
        {
          id: "cost",
          title: "Infra Spend",
          subtitle: "This Month",
          value: `₹${stats.infraSpend || 0}`,
          icon: <InfraSpendIcon />,
        },
        {
          id: "team",
          title: "Top Performer",
          subtitle: "Supervisor",
          value: stats.topSupervisor || "N/A",
          icon: <TopPerformerIcon />,
        },
      ]
    : isManagerStats
    ? [
        {
          id: "total",
          title: "Total Tickets",
          subtitle: "Total",
          value: stats.totalTickets || 0,
          icon: <AssignmentIcon />,
        },
        {
          id: "opened",
          title: "Total Opened",
          subtitle: "Tickets",
          value: stats.ticketsAwaitingAssignment || 0,
          icon: <TicketsRaisedIcon />,
        },
        {
          id: "closed",
          title: "Closed This Week",
          subtitle: "Tickets",
          value: stats.ticketsResolved || 0,
          icon: <CompletedIcon />,
        },
        {
          id: "reopened",
          title: "Reopened",
          subtitle: "Tickets",
          value: stats.ticketsReopened || 0,
          icon: <ReopenIcon />,
        },
        {
          id: "resolution",
          title: "Avg. Resolution Time",
          subtitle: "Overall",
          value: stats.avgResolutionTime || "N/A",
          icon: <ClockIcon />,
        },
      ]
    : isSupervisorStats
    ? [
        {
          id: "total",
          title: "Total Tickets",
          subtitle: "Total",
          value: stats.totalTickets || 0,
          icon: <AssignmentIcon />,
        },
        {
          id: "assigned",
          title: "Assigned Tickets",
          subtitle: "Awaiting work",
          value: stats.assignedTickets || 0,
          icon: <AssignmentIcon />,
        },
        {
          id: "inProgress",
          title: "In Progress",
          subtitle: "Currently working",
          value: stats.inProgressTickets || 0,
          icon: <InProgressIcon />,
        },
        {
          id: "completed",
          title: "Work Completed",
          subtitle: "Awaiting approval",
          value: stats.ticketsCompleted || 0,
          icon: <CompletedIcon />,
        },
        {
          id: "resolution",
          title: "Avg. Resolution Time",
          subtitle: "This month",
          value: stats.avgResolutionTime,
          icon: <ClockIcon />,
        },
      ]
    : [
        {
          id: "total",
          title: "Total Tickets",
          subtitle: "Total",
          value: stats.totalTickets || 0,
          icon: <AssignmentIcon />,
        },
        {
          id: "raised",
          title: "Tickets Raised",
          subtitle: "This Month",
          value: stats.ticketsRaised || 0,
          icon: <TicketsRaisedIcon />,
        },
        {
          id: "closed",
          title: "Tickets Closed",
          subtitle: "Total",
          value: stats.ticketsClosed || 0,
          icon: <CompletedIcon />,
        },
        {
          id: "resolution",
          title: "Avg. Resolution",
          subtitle: "Time",
          value: stats.avgResolutionTime || "-",
          icon: <ClockIcon />,
        },
        {
          id: "reopened",
          title: "Tickets Reopened",
          subtitle: "This Month",
          value: stats.ticketsReopened || 0,
          icon: <ReopenIcon />,
        },
      ];

  return (
    <div
      className={`stats-card-section ${
        window.innerWidth > 768
          ? statsData.length === 5
            ? "fiveGridSystem"
            : "fourGridSystem"
          : ""
      }`}
    >
      {statsData.map((stat) => (
        <div key={stat.id} className="stat-card">
          <div className="stat-card-header">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-title-header">
              <div className="primary-title">{stat.title}</div>
              <div className="subtitle">({stat.subtitle})</div>
            </div>
          </div>
          <div className="stat-card-body">
            <div
              className={`${
                stat?.subtitle === "Supervisor" ? "text-value" : "stat-value"
              }`}
            >
              {stat.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsCardSection;
